import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUrlPathLocation } from '@medifind/hooks';
import { getSpecialtyBySlug, wpPostsByCategory } from '@medifind/interface';
import { useRouter } from '@medifind/router';
import { fillTemplate, stripAllHtml } from '@medifind/utils';
import { setAdditionalRouteParams, useRouteStore } from '@medifind/zustand';

export const useSpecialtyParams = () => {
  const router = useRouter();

  const routeUUID = useRouteStore((state) => state.routeUUID);
  const { specialtySlug } = router.query;
  const { urlLocation } = useUrlPathLocation();

  /** *****************************************************
   * START SPECIALTY - Get data for specialty header based off the slug
   */
  const { data: specialtyDataCache, isPending: isPendingSpecialtyData } = useQuery({
    queryKey: ['specialtyData', specialtySlug],
    queryFn: async () => {
      const { specialty, role, slug } = await getSpecialtyBySlug(specialtySlug.toLowerCase()).catch(() => ({}));
      if (!specialty || !role) {
        return { notFound: true };
      }

      const nameString = role ? role : specialty;
      return { specialty, role, specialtySlug: slug, nameString };
    },
    enabled: !!specialtySlug,
    staleTime: Infinity,
  });

  const { data: wordpressData, isPending: isPendingWordpressData } = useQuery({
    queryKey: ['wordpressData', specialtySlug],
    queryFn: ({ signal }) =>
      wpPostsByCategory({
        params: {
          getContent: false,
          category: 'specialty-page',
          page: 1,
          perPage: 1,
          specialty: specialtySlug,
        },
        signal,
      }).catch(() => null),
    select: (data) => data?.results[0] ?? {},
    enabled: !!specialtySlug,
    staleTime: Infinity,
  });

  const isPending = isPendingSpecialtyData || isPendingWordpressData;

  useEffect(() => {
    const specialtyData = { ...specialtyDataCache }; // Make a copy so we dont change the cached data
    if (!isPending) {
      if (specialtyData && wordpressData) {
        const { role: specialtyRole, specialty, nameString } = specialtyData;
        specialtyData.pageTitle = fillTemplate(
          '25 of the Best [specialtyRole:[specialtyRole]]s[!mediumLocationString: Near Me][stateProvinceCode: Near Me][mediumLocationString: in][mediumLocationString: [mediumLocationString]]',
          {
            ...urlLocation,
            specialtyRole,
            specialty,
          },
        );
        specialtyData.codingList = wordpressData?.acf?.codingList?.map(({ coding }) => coding) ?? [];

        let pageDescription = wordpressData?.acf?.pageDescription ? stripAllHtml(wordpressData.acf.pageDescription) : null;
        if (pageDescription) {
          pageDescription = fillTemplate(pageDescription, { ...urlLocation });
        } else {
          pageDescription = fillTemplate(
            `Find the best [specialtyRole:[specialtyRole]]s[!mediumLocationString: Near Me][stateProvinceCode: Near Me][mediumLocationString: in][mediumLocationString: [mediumLocationString]] with MediFind. A guide for how to find the top ${nameString} [!mediumLocationString: near me][city: near][mediumLocationString&!city: in][mediumLocationString: [mediumLocationString]].`,
            {
              ...urlLocation,
              specialtyRole,
              specialty,
            },
          );
        }
        specialtyData.pageDescription = pageDescription;

        setAdditionalRouteParams({ specialty: specialtyData }, routeUUID);
      } else if (specialtyData?.notFound) {
        setAdditionalRouteParams({ specialty: specialtyData }, routeUUID);
      }
    }
  }, [specialtyDataCache, urlLocation, routeUUID, wordpressData, isPending]);
  /** *****************************************************
   * END SPECIALTY
   */

  return null;
};
